import { createSelector } from '@reduxjs/toolkit';
import { getBookingState } from 'library/helpers/bookingHelpers';
import dateFunctions from 'library/helpers/dateFunctions';
import { selectSettingsRoomConfiguration } from 'reduxStore/organisations/selectors';

// 📌 Selector to get all bookings from the current profile
export const selectBookings = createSelector(
	[(state) => state.Bookings.data, (state) => state.Auth.selectedProfile.orgID],
	(bookingsData, orgID) => bookingsData[orgID] || []
);

// 📌 Selector to get a booking by bookingID
export const selectBookingByBookingID = createSelector(
	[(state) => state.Bookings.data, (state) => state.Auth.selectedProfile.orgID, (_, bookingID) => bookingID],
	(bookingsData, orgID, bookingID) =>
		(bookingsData[orgID] || []).find((booking) => booking.bookingID === bookingID) || null
);

// 📌 Selector to get all bookings assigned to a specific person
export const selectBookingsByPersonID = createSelector(
	[
		(state) => state.Bookings.data,
		(state) => state.Auth.selectedProfile.orgID,
		(_, assignedToPerson) => assignedToPerson
	],
	(bookingsData, orgID, assignedToPerson) =>
		(bookingsData[orgID] || []).filter((booking) => booking.assignedToPerson === assignedToPerson) || []
);

// 📌 Selector to get all bookings for a specific device
export const selectBookingsByDeviceID = createSelector(
	[(state) => state.Bookings.data, (state) => state.Auth.selectedProfile.orgID, (_, deviceID) => deviceID],
	(bookingsData, orgID, deviceID) =>
		(bookingsData[orgID] || []).filter((booking) => booking.deviceID === deviceID) || []
);

// 📌 Selector to get all bookings by status (completed or active)
export const selectBookingsByPersonAndState = createSelector(
	[(state, personID) => state.Bookings.bookings[personID]], // Access array directly
	(bookings = []) => bookings.filter((b) => ['Upcoming', 'Active'].includes(getBookingState(b)))
);

// 📌 Selector to get all bookings by type (e.g., Smart Locker, Guest Permission, etc.)
export const selectBookingsByTypeID = createSelector(
	[(state) => state.Bookings.data, (state) => state.Auth.selectedProfile.orgID, (_, bookingTypeID) => bookingTypeID],
	(bookingsData, orgID, bookingTypeID) =>
		(bookingsData[orgID] || []).filter((booking) => booking.bookingTypeID === bookingTypeID) || []
);

// 📌 Selector to get booking date configuration by organization
export const selectBookingDateConfigByOrg = createSelector([selectSettingsRoomConfiguration], (roomConfig) => {
	const categories = roomConfig?.categories || [];

	return categories.reduce((acc, cat) => {
		const cfg = cat?.bookingConfiguration;

		if (cfg?.start || cfg?.end) {
			acc[cat.ID] = {
				maxBookingValueDate: dateFunctions.calculateDate(cfg?.end),
				minBookingValueDate: dateFunctions.calculateDate(cfg?.start),
				minBookingDate: cfg?.start,
				maxBookingDate: cfg?.end
			};
		} else {
			acc[cat.ID] = {
				maxBookingValueDate: dateFunctions[cat?.maxBookingDate || 'twelveMonthsFromToday'](),
				minBookingValueDate: dateFunctions[cat?.minBookingDate || 'today'](),
				minBookingDate: cat?.minBookingDate,
				maxBookingDate: cat?.maxBookingDate
			};
		}

		return acc;
	}, {});
});

// 📌 Selector to get cleaning configuration by organization
export const selectCleaningConfigByOrg = createSelector([selectSettingsRoomConfiguration], (roomConfig) => {
	const categories = roomConfig?.categories || [];

	return categories.reduce((acc, cat) => {
		const cfg = cat?.cleaningConfiguration || cat?.cleaning || {};

		acc[cat.ID] = {
			start: cfg.start ?? null,
			end: cfg.end ?? null
		};

		return acc;
	}, {});
});
